import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Alert,
} from '@mui/material';
import DynamicPlotlyChart from './DynamicPlotlyChart';
import TypingText from './TypingText';
import DownloadCSV from './DownloadCSV';
import { useSelector } from 'store/hooks';

const DynamicMessageCard = ({ isMaximized }) => {
  const maxWidth = isMaximized ? 560 : 308;

  const { conversationList } = useSelector((state) => state.ai);

  return (
    <>
      {conversationList?.map((item: any, index: number) => (
        <Box key={item?.id}>
          {/* QUERY */}
          {item?.type === 'query' && (
            <Box display="flex" justifyContent="flex-end">
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 1,
                  borderRadius: 2,
                  backgroundColor: '#ECF4FF',
                  maxWidth,
                  wordBreak: 'break-word',
                }}
              >
                {item?.msg}
              </Paper>
            </Box>
          )}

          {/* GENERAL / DATABASE */}
          {(item?.type === 'general' || item?.type === 'database') && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 1,
                borderRadius: 2,
                backgroundColor: '#F6F6F6',
                maxWidth,
                wordBreak: 'break-word',
              }}
            >
              {conversationList?.length - 1 === index ? <TypingText text={item?.msg} speed={20} /> : item?.msg}
            </Paper>
          )}

          {item?.type === 'response' &&
            (() => {
              const severity = 'error';

              return (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 1,
                    borderRadius: 2,
                    backgroundColor: '#F6F6F6',
                    maxWidth,
                    wordBreak: 'break-word',
                  }}
                >
                  <Stack spacing={1}>
                    <Alert icon={false} severity={severity} sx={{ marginBottom: 1 }}>
                      {item?.msg}
                    </Alert>
                  </Stack>
                </Paper>
              );
            })()}

          {/* Confidence Level */}
          {item?.type === 'confidencelevel' &&
            (() => {
              let severity: 'success' | 'info' | 'warning' | 'error' = 'info';

              if (typeof item?.msg === 'string') {
                const msgLower = item.msg.toLowerCase();
                if (msgLower.includes('medium')) {
                  severity = 'error';
                } else if (msgLower.includes('high')) {
                  severity = 'warning';
                } else if (msgLower.includes('low')) {
                  severity = 'success';
                }
              }

              return (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 1,
                    borderRadius: 2,
                    backgroundColor: '#F6F6F6',
                    maxWidth,
                    wordBreak: 'break-word',
                  }}
                >
                  <Stack spacing={1}>
                    <Alert icon={false} severity={severity} sx={{ marginBottom: 1 }}>
                      {`Confidence Level: ${item?.msg}`}
                    </Alert>
                  </Stack>
                </Paper>
              );
            })()}

          {/* EXPLANATION */}
          {item?.type === 'explaination' && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 1,
                borderRadius: 2,
                backgroundColor: '#F6F6F6',
                maxWidth,
                wordBreak: 'break-word',
              }}
            >
              <Stack spacing={1}>
                {item?.msg?.split('\n').map((line: any, lineIndex: number) => (
                  <Typography key={line}>{line}</Typography>
                ))}
              </Stack>
            </Paper>
          )}

          {/* GRAPH */}
          {item?.type === 'graph' && (
            <DynamicPlotlyChart
              chartData={item?.msg}
              width={isMaximized ? '600' : '350'}
              height={isMaximized ? '600' : '350'}
            />
          )}

          {/* REPORT */}
          {item?.type === 'report' && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 1,
                borderRadius: 2,
                backgroundColor: '#F6F6F6',
                maxWidth,
              }}
            >
              <Typography>The requested report has been generated successfully.</Typography>

              <Box
                mt={3}
                sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                  boxShadow: 1,
                  overflow: 'hidden',
                  maxWidth,
                }}
              >
                <TableContainer sx={{ maxHeight: 200 }}>
                  <Table stickyHeader size="small">
                    <TableHead sx={{ backgroundColor: '#f3f4f6' }}>
                      <TableRow>
                        {Object.keys(item?.msg).map((key) => (
                          <TableCell key={key}>
                            {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.keys(item?.msg[Object.keys(item?.msg)[0]]).map((rowKey) => (
                        <TableRow key={rowKey}>
                          {Object.keys(item?.msg).map((colKey) => (
                            <TableCell key={colKey}>{item?.msg[colKey][rowKey]}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box mt={1}>
                <DownloadCSV data={item?.msg} />
              </Box>
            </Paper>
          )}
        </Box>
      ))}
    </>
  );
};

export default DynamicMessageCard;
